import { useCallback, useRef, useState } from 'react';
import cn from 'classnames';
import { Popover, PopoverContent, PopoverTrigger, addToast } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { useShareStudentBadgeMutation, type IStudentBadge } from '@/entities/Badges';
import { Svg } from '@/shared/ui/Svg';
import { useModalAutoFocus } from '@/shared/lib/hooks';
import { copyTextToClipboard } from '@/shared/lib/utils/copyTextToClipboard';
import { getErrorMessage } from '@/shared/lib/utils/getErrorMessage';
import type { RejectApiResponse } from '@/shared/types/common';
import ForwardIcon from '@/shared/assets/icons/forward.svg';
import { downloadBadgeImage } from '../lib/downloadBadgeImage';
import { SHARE_TARGETS, buildShareText, openShareTarget } from '../lib/shareTargets';

interface BadgeShareButtonProps {
  badge: IStudentBadge;
}

const itemClass =
  'flex w-full items-center gap-3 rounded-[10px] px-3 py-2 text-left text-sm font-medium ' +
  'text-black transition-colors hover:bg-black/[0.04] disabled:opacity-50';

export const BadgeShareButton = ({ badge }: BadgeShareButtonProps) => {
  const { t } = useTranslation();
  const [shareBadge, { isLoading }] = useShareStudentBadgeMutation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Кнопка не может быть PopoverTrigger: он открывает меню сам, и на телефоне рядом
  // с системным окном вылезал бы ещё и наш список. Поэтому у меню отдельный якорь,
  // а клики обрабатывает кнопка.
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Ссылка постоянная, поэтому за сессию хватает одного запроса
  const [shareUrl, setShareUrl] = useState('');

  const text = buildShareText(badge);

  // Поповер идёт через тот же Overlay, что и модалка: фокус в него не переходит
  useModalAutoFocus(isMenuOpen);

  const ensureShareUrl = useCallback(async () => {
    if (shareUrl) return shareUrl;

    const result = await shareBadge(badge.id).unwrap();
    setShareUrl(result.shareUrl);
    return result.shareUrl;
  }, [badge.id, shareBadge, shareUrl]);

  const handleOpen = async () => {
    try {
      // Системное окно не зовём: на Windows canShare уже есть, и вместо нашего списка
      // открывалась общая панель обмена с приложениями из Store.
      await ensureShareUrl();
      setIsMenuOpen(true);
    } catch (error) {
      addToast({
        title:
          getErrorMessage(error as RejectApiResponse | undefined) ??
          t('badges.share.linkFailed'),
        color: 'danger',
      });
    }
  };

  const handleTarget = (href: string) => {
    openShareTarget(href);
    setIsMenuOpen(false);
  };

  const handleCopy = async () => {
    setIsMenuOpen(false);
    try {
      await copyTextToClipboard(shareUrl);
      addToast({ title: t('badges.share.linkCopied'), color: 'success' });
    } catch {
      addToast({ title: t('badges.share.copyFailed'), color: 'danger' });
    }
  };

  const handleDownload = async () => {
    setIsMenuOpen(false);
    try {
      await downloadBadgeImage(badge);
    } catch {
      addToast({ title: t('badges.share.imageFailed'), color: 'danger' });
    }
  };


  return (
    <span className="relative inline-flex h-6 w-6">
      <button
        ref={triggerRef}
        type="button"
        onClick={handleOpen}
        disabled={isLoading}
        aria-label={t('badges.share.trigger', { title: badge.title })}
        aria-haspopup="menu"
        aria-expanded={isMenuOpen}
        className={cn(
          'inline-flex h-6 w-6 cursor-pointer items-center justify-center text-grey-secondary',
          'transition-colors hover:text-grey disabled:cursor-not-allowed disabled:opacity-50',
        )}
      >
        <Svg Icon={ForwardIcon} width={20} height={20} />
      </button>
      <Popover isOpen={isMenuOpen} onOpenChange={setIsMenuOpen} placement="bottom-end">
        {/* Якорь для позиционирования: кликов он не получает, их обрабатывает кнопка выше. */}
        <PopoverTrigger>
          <span aria-hidden className="pointer-events-none absolute inset-0" />
        </PopoverTrigger>
        <PopoverContent className="w-[220px] items-stretch gap-1 rounded-2xl p-2">
          {SHARE_TARGETS.map((target) => (
            <button
              key={target.id}
              type="button"
              className={itemClass}
              onClick={() => handleTarget(target.href(shareUrl, text))}
            >
              {target.label}
            </button>
          ))}
          <div className="my-1 h-px w-full bg-grey-modern" />
          <button type="button" className={itemClass} onClick={handleCopy}>
            {t('badges.share.copyLink')}
          </button>
          <button type="button" className={itemClass} onClick={handleDownload}>
            {t('badges.share.downloadImage')}
          </button>
        </PopoverContent>
      </Popover>
    </span>
  );
};
